import dayjs from "dayjs";
import "./RenderPdf.css";
import utc from "dayjs/plugin/utc";
import { advices, BengaliAdvices } from "../selectOptionValues";
import { getAgeCategory, getAgeCategoryByDays } from "../../helper/getAgeGroup";
import { useEffect, useState } from "react";
import { languageCodes } from "../../locals/languageCode";
import { useParentContext } from "../../context/ParentContext";

const RenderPdf = ({
  data,
  index,
  vaccines,
  milestones,
  patientName,
  isMobile,
  mchatScore,
  mchatStatus,
  mchatTableData,
}) => {
  dayjs.extend(utc);

  const [translations, setTranslations] = useState(null);
  const { language } = useParentContext();
  const getLanguageData = async (selectedLanguage) => {
    try {
      const languageModule = await import(
        `../../locals/pdfLanguages/${selectedLanguage}.json`
      );
      return languageModule.default;
    } catch (error) {
      console.error(`Error loading ${selectedLanguage} translations:`, error);
      return null;
    }
  };
  
  useEffect(() => {
    const loadTranslations = async () => {
      try {
        const data = await getLanguageData(languageCodes[language]);
        setTranslations(data);
      } catch (error) {
        console.error("Error loading translations:", error);
      }
    };
    
    loadTranslations();
  }, [language]);

  const t = (milestone) => {
    if (!milestone || !translations) return milestone;
      return translations[milestone] || milestone;
  };

  
  const ageGroup =
    Number(data.patient_age_in_months) === 0
      ? getAgeCategoryByDays(data?.age_days)
      : getAgeCategory(data.patient_age_in_months);
  const pdfFormatDate = (dateString) => {
    if (!dateString || dateString === "None") return "N/A";
    const date = dayjs.utc(dateString).local();
    return date.format("HH:mm:ss") === "00:00:00" ||
      date.format("HH:mm:ss") === ""
      ? date.format("DD MMM YYYY")
      : date.format("DD MMM YYYY hh:mm");
    };

    const pdfAdvices = (language === 'bn' ? BengaliAdvices[ageGroup] : advices[ageGroup])
    
  const pdfFormatOnlyDate = (dateString) => {
    if (!dateString || dateString === "None") return "N/A";
    const date = dayjs.utc(dateString).local();
    return date.format("DD MMM YYYY");
  };
  const sections = [
    {
      title: "ANTHROPOMETRY",
      data: [
        { heading: "Weight (kg)", value: data.weight_kg },
        { heading: "Height (cm)", value: data.height_cm },
        {
          heading: "Head Circumference (cm)",
          value: data.head_circumference_cm,
        },
      ],
    },
    {
      title: "VITALS",
      data: [
        {
          heading: "Heart Rate (bpm)",
          value: data.heart_rate ? data.heart_rate : null,
        },
        { heading: "Respiration Rate", value: data.respiration_rate },
      ],
    },
    {
      title: "GENERAL APPEARANCE",
      data: [
        { heading: "Dysmorphism", value: data.dysmorphism },
        { heading: "Asymmetry", value: data.asymmetry },
        { heading: "Abnormal Head Size", value: data.abnormal_head_size },
        { heading: "Skin Lesion", value: data.skin_lesion },
        { heading: "Malnutrition", value: data.malnutrition },
        {
          heading: "Teeth erupting abnormally",
          value: data.teeth_erupting_normally,
        },
      ],
    },
    {
      title: "SYSTEMIC",
      data: [
        { heading: "Anaemia", value: data.anaemia },
        { heading: "Jaundice", value: data.jaundice },
        { heading: "Rickets", value: data?.rickets },
        {
          heading: "Vitamin D prophylaxis",
          value: data?.vitamin_d_prophylaxis,
        },
        { heading: "Bowel Disturbance", value: data.bowel_disturbance },
        {
          heading: "Hepato Splenomegaly",
          value: data.hepato_splenomegaly,
        },
        { heading: "Tone", value: data.tone },
        { heading: "Power", value: data.npower },
        { heading: "Reflexes", value: data.reflexes },
      ],
    },
    {
      title: "NUTRITION",
      data: [
        { heading: "Breastfeeding", value: data.breastfeeding },
        { heading: "Feeding technique", value: data.feeding_technique },
        { heading: "Weight gain", value: data.weight_gain },
      ],
    },
    {
      title: "SAFETY/SECURITY",
      data: [
        { heading: "Baby proofing", value: data.baby_proofing },
        {
          heading: "Signs of neglect",
          value: data.signs_of_neglect,
        },
      ],
    },
    {
      title: "RESPONSIVE CARE-GIVING",
      data: [
        {
          heading: "How do parents interact with the baby",
          value:
            data.responsive_care_giving &&
            data.responsive_care_giving.join(", "),
        },
      ],
    },
    {
      title: "EARLY LEARNING OPPORTUNITY",
      data: [
        {
          heading: "Early learning opportunity",
          value:
            data.early_learning_opportunity &&
            data.early_learning_opportunity.join(", "),
        },
      ],
    },
    {
      title: "ANY RED FLAGS",
      data: [
        {
          heading: "Any red flags",
          value: data.any_red_flags && data.any_red_flags.join(", "),
        },
      ],
    },
    {
      title: "COMMENTS",
      data: [{ heading: "Comments", value: data.notes }],
    },
  ];
  const impressionData = [
    { heading: "Delayed milestones", value: data.delayed_milestones },
    { heading: "Difficulty in movement", value: data.difficulty_in_movement },
    { heading: "Stiffness of body", value: data.stiffness_of_body },
    { heading: "Seizures", value: data.seizures },
    { heading: "Impaired vision", value: data.impaired_vision },
    { heading: "Impaired Hearing", value: data.impaired_hearing },
  ].filter((item) => item.value && item.value !== "None");

  const last10 = milestones.slice(-10);

  const milestoneList = last10
    .map((item) => item.Milestone?.trim())
    .filter((val) => val && val.toLowerCase() !== "none");

  const redFlagList = last10
    .map((item) => item.Red_Flag?.trim() || item["Red Flag"]?.trim())
    .filter((val) => val && val.toLowerCase() !== "none");

  const shouldIncludeSection = (section) =>
    section.data.some(
      (item) =>
        item?.value &&
        !["None", "0", "0.00", "no", "No", null, ""].includes(item.value)
    );

  const vaccinationData = vaccines
    .map((vaccine) => ({
      vaccine: vaccine.Vaccine?.trim(),
      dateWhenTaken: pdfFormatOnlyDate(vaccine["Date When Taken"]),
    }))
    .filter(
      (item) =>
        (item.vaccine && item.vaccine !== "None") ||
        (item.dateWhenTaken && item.dateWhenTaken !== "None")
    );

  return (
    <div
      id={`pdf-content-${index}`}
      style={{
        transform: "scale(1)",
      }}
      className="pdf-content"
    >
      <h2 className="pfd-heading">Patient Visit Summary</h2>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <p>
          <span>Reason for Visit:</span>{" "}
          <span translate="no">{data?.reason_for_visit || t("Wellness Visit")}</span>
        </p>
        <p className="pdf-dateOfVisit">
          <span>Date of visit:</span> {pdfFormatDate(data.visit_date_time)}
        </p>
      </div>

      <div className="pdf-patient-info">
        <p>
          <span>Patient Name:</span> {patientName}
        </p>
        <p>
          <span>
            {data.patient_age_in_months === 0
              ? "Patient Age (Days)"
              : "Patient Age (Months)"}
            :
          </span>
          {data.patient_age_in_months === 0
            ? data.age_days
            : data.patient_age_in_months}
        </p>
        <p>
          <span translate="no">
            {data.gestational_age_in_months === 0
              ? t("Adjusted Age (Days)")
              : t("Adjusted Age (Months)")}
            :
          </span>
          {(data.gestational_age_in_months === 0
            ? data.gestational_age_in_days
            : data.gestational_age_in_months) || "N/A"}
        </p>
        <p>
          <span>Follow-up Date:</span> {pdfFormatDate(data.follow_up_date)}
        </p>
      </div>
      <div className="pdf-health-summary">
        <h4>PATIENT HEALTH SUMMARY</h4>
        {data.ai_health_summary
          .split("\n")
          .filter((line) => line.trim() !== "")
          .map((line, index) => {
            const shouldNotTranslate =
              line.trim().startsWith("Name") ||
              line.trim().startsWith("Patient Name");
            return (
              <p
                key={index}
                {...(shouldNotTranslate ? { translate: "no" } : {})}
              >
                {line}
              </p>
            );
          }) || "No PATIENT HEALTH SUMMARY available"}
      </div>
      {!data?.reason_for_visit &&
        sections.map(
          (section, i) =>
            shouldIncludeSection(section) && (
              <div key={i} className="pdf-section">
                <h4 translate='no'>{t(section.title)}</h4>
                <table className="pdf-table">
                  <thead>
                    <tr>
                      <th>{"Attribute"}</th>
                      <th>{"Value"}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.data.map(
                      (row, j) =>
                        row.value &&
                        !["None", "0", "0.00", "no", "No", null, ""].includes(
                          row.value
                        ) && (
                          <tr key={j}>
                            <td translate={row.heading==='Any red flags' ? 'no' : 'yes'}>{t(row.heading)}</td>
                            <td>{row.value}</td>
                          </tr>
                        )
                    )}
                  </tbody>
                </table>
              </div>
            )
        )}
      {!data?.reason_for_visit && impressionData.length > 0 && (
        <div className="pdf-section">
          <h4 translate="no">{t("IMPRESSION")}</h4>
          <table className="pdf-table">
            <thead>
              <tr>
                <th>{"Attribute"}</th>
                <th>{"Value"}</th>
              </tr>
            </thead>
            <tbody>
              {impressionData.map((row, idx) => (
                <tr key={idx}>
                  <td>{row.heading}</td>
                  <td>{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!data.reason_for_visit && pdfAdvices && (
        <div className="advice-section">
          <h4 className="advice-heading">ADVICE</h4>
          <div className="advice-list">
            {pdfAdvices
              .split("\n")
              .filter((line) => line.trim() !== "")
              .map((line, idx) => (
                <div className="advice-item" key={idx}>
                  <p>{line.replace(/^•/, "→ ").trim()}</p>
                </div>
              ))}
          </div>
        </div>
      )}
      {!data?.reason_for_visit &&
        (milestoneList.length > 0 || redFlagList.length > 0) && (
          <div className="milestone-section">
            <h4 translate="no">{t("MILESTONES & RED FLAGS")}</h4>
            <div className="milestones-grid">
              <div className="milestone-column">
                <h4 translate="no">{t("Milestones")}</h4>
                <ul>
                  {milestoneList.map((milestone, idx) => (
                    <li key={idx}>{milestone}</li>
                  ))}
                </ul>
              </div>
              <div className="red-flag-column">
                <h4 translate="no"> {t("Red Flags")}</h4>
                <ul>
                  {redFlagList.map((redFlag, idx) => (
                    <li style={{color:'red/50'}} key={idx}>{redFlag}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}
      {!data?.reason_for_visit && vaccinationData.length > 0 && (
        <div className="pdf-section" id="vaccination-section">
          <h4>VACCINATION TAKEN</h4>
          <table className="pdf-table">
            <thead>
              <tr>
                <th>Vaccine</th>
                <th>Date When Taken</th>
              </tr>
            </thead>
            <tbody>
              {vaccinationData.map((row, idx) => (
                <tr key={idx}>
                  <td>{row.vaccine || ""}</td>
                  <td>{row.dateWhenTaken || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {!data?.reason_for_visit && (
        <div className="chart-wrapper">
          <h4>Weight for Age Chart</h4>
          <div
            className="chart-capture"
            data-chart-id={`weight-chart-${index}`}
          />
        </div>
      )}

      {!data?.reason_for_visit && (
        <div className="chart-wrapper">
          <h4>Length for Age Chart</h4>
          <div
            className="chart-capture"
            data-chart-id={`height-chart-${index}`}
          />
        </div>
      )}

      {!data?.reason_for_visit && (
        <div className="chart-wrapper">
          <h4>Head Circumference for Age Chart</h4>
          <div
            className="chart-capture"
            data-chart-id={`head-circ-chart-${index}`}
          />
        </div>
      )}

      {!data?.reason_for_visit && (
        <div className="chart-wrapper">
          <h4>Weight for Length Chart</h4>
          <div
            className="chart-capture"
            data-chart-id={`weight-height-chart-${index}`}
          />
        </div>
      )}

      {!data?.reason_for_visit && (
        <div className="chart-wrapper">
          <h4>TDSC CHART</h4>

          {/* Always render both charts for PDF generation */}
          <div
            className={`chart-capture pdfTdscMobileView-${index}`}
            style={{ display: isMobile ? "block" : "none" }}
          >
            {/* Mobile chart */}
          </div>

          <div
            className="chart-capture tdscDesktopViewpdf"
            style={{ display: !isMobile ? "block" : "none" }}
          >
            {/* Desktop chart */}
          </div>
        </div>
      )}
      {!data?.reason_for_visit && mchatScore !== null && (
        <div className="mchat-section page-break-before">
          <div style={{ pageBreakInside: "avoid" }}>
            <h4 style={{ textAlign: "center", paddingTop: "8px" }}>MCHAT</h4>
            <p>
              <strong>MCHAT Score:</strong> {mchatScore}
            </p>
            <p>
              <strong>Status:</strong> MCHAT status -{" "}
              {mchatStatus === "Low Risk"
                ? "Low Risk"
                : mchatStatus === "Moderate Risk"
                ? "Moderate Risk (Please get M-CHAT tested after 6 months)"
                : mchatStatus === "High Risk"
                ? "High Risk (Please consult an Early Development Specialist)"
                : mchatStatus || "Not Available"}
            </p>
          </div>

          <table border="1" cellSpacing="0">
            <thead>
              <tr>
                <th style={{ textAlign: "left" }}>Question</th>
                <th style={{ textAlign: "left" }}>Answer</th>
              </tr>
            </thead>
            <tbody>
              {mchatTableData.map((item, index) => (
                <tr key={index}>
                  <td style={{ whiteSpace: "pre-wrap" }}>{item.heading}</td>
                  <td>{item.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default RenderPdf;